import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { VehiclePicker } from "@/components/VehiclePicker";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertTriangle, Info, Wrench, BellPlus } from "lucide-react";
import { toast } from "sonner";

const TIPS = [
  { q: "What is offset (ET) and why does it matter?", a: "Offset is how far the wheel's mounting face sits from its centerline. Lower/negative offset pushes the wheel outward (more 'poke') and can rub fenders; higher offset tucks it in and may rub suspension. Match the listing's offset to what your build runs." },
  { q: "When do I need wheel spacers?", a: "Spacers push the wheel outward to clear big brakes, suspension, or to fine-tune stance. They don't change the bolt pattern — they add clearance. Use hub-centric spacers and extended studs for safety." },
  { q: "What about wheel adapters?", a: "Adapters let a wheel with a DIFFERENT bolt pattern bolt onto your hub (e.g. running a 6x135 wheel on a 6x139.7 truck). They're a spacer + new bolt pattern in one. Buy quality, hub-centric adapters." },
  { q: "Will oversized tires fit?", a: "Tire fitment depends on diameter, width, offset, and your suspension/trimming. A 35\" tire on a stock truck often needs a leveling kit; 37\"+ usually needs a lift and trimming." },
];

export const FitmentChecker = ({ listing }) => {
  const [resolved, setResolved] = useState(null);
  const [saved, setSaved] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const saveTruckSearch = async () => {
    if (!user) { navigate("/login"); return; }
    try {
      await api.post("/saved-searches", {
        name: `${resolved.label} fitment`,
        filters: { bolt_pattern: listing.bolt_pattern, category: listing.category },
        alerts_enabled: true,
      });
      setSaved(true);
      toast.success("Saved! We'll alert you when matching listings drop.");
    } catch {
      toast.error("Could not save search");
    }
  };

  const isTires = listing.category === "tires";
  let verdict = null;
  if (resolved) {
    if (isTires || !listing.bolt_pattern) {
      verdict = { level: "info", title: "Check wheel diameter", note: `Your ${resolved.label} typically runs ${resolved.diameters?.join('", ')}" wheels. Make sure these tires match your current wheel size.` };
    } else if (resolved.bolt_pattern === listing.bolt_pattern) {
      const fits = !resolved.diameters || !listing.diameter || resolved.diameters.includes(String(listing.diameter));
      verdict = fits
        ? { level: "ok", title: "Direct Fit", note: `${listing.bolt_pattern} matches your ${resolved.label}. Confirm offset clearance for your suspension setup.` }
        : { level: "warn", title: "Bolt pattern fits — check diameter", note: `${listing.bolt_pattern} matches, but ${listing.diameter}" is outside the typical ${resolved.diameters.join('", ')}" range for your ${resolved.label}. Verify brake & fender clearance.` };
    } else {
      verdict = { level: "warn", title: "Needs wheel adapters", note: `This wheel is ${listing.bolt_pattern} but your ${resolved.label} is ${resolved.bolt_pattern}. It will fit with quality hub-centric adapters — it won't bolt on directly.` };
    }
  }

  const styles = {
    ok: { icon: CheckCircle2, cls: "border-emerald-600/50 bg-emerald-950/30 text-emerald-400" },
    warn: { icon: AlertTriangle, cls: "border-amber-600/50 bg-amber-950/30 text-amber-400" },
    info: { icon: Info, cls: "border-blue-600/50 bg-blue-950/30 text-blue-400" },
  };
  const V = verdict ? styles[verdict.level] : null;

  return (
    <div className="bg-[#141414] border border-border rounded-sm p-4">
      <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-3">
        <Wrench className="w-3.5 h-3.5 text-blaze" /> Will this fit my truck?
      </div>
      <VehiclePicker onResolve={setResolved} onReset={() => setResolved(null)} />

      {verdict && (
        <div data-testid="fitment-verdict" className={`mt-4 border rounded-sm p-3 flex items-start gap-2.5 ${V.cls}`}>
          <V.icon className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <div className="font-heading font-800 text-sm uppercase tracking-tight">{verdict.title}</div>
            <p className="text-xs mt-1 leading-relaxed opacity-90">{verdict.note}</p>
          </div>
        </div>
      )}

      {verdict?.level === "ok" && !isTires && listing.bolt_pattern && (
        <Button
          data-testid="save-fitment-search-btn"
          onClick={saveTruckSearch}
          disabled={saved}
          className="w-full mt-3 bg-blaze hover:bg-blaze-hover text-white rounded-sm font-bold gap-2 disabled:opacity-60"
        >
          <BellPlus className="w-4 h-4" /> {saved ? "Search saved" : `Alert me on more ${resolved.label} fitment`}
        </Button>
      )}

      <Accordion type="single" collapsible className="mt-4">
        <AccordionItem value="tips" className="border-border">
          <AccordionTrigger data-testid="spacer-tips-trigger" className="text-xs uppercase tracking-wider text-muted-foreground hover:text-blaze hover:no-underline py-2">Spacer & fitment tips</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 pt-1">
              {TIPS.map((t, i) => (
                <div key={i}>
                  <div className="text-xs font-bold text-white">{t.q}</div>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{t.a}</p>
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};
